import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "@/store/DataContext";
import {
  ChipGroup,
  CurrencySegment,
  Field,
  NumberInput,
  PrimaryButton,
  TextArea,
  TextInput,
} from "@/components/base/FormControls";
import SelectSheet, { type SelectOption } from "@/components/base/SelectSheet";
import { money, number, todayISO, uid } from "@/lib/format";
import type { Currency, ReturnReason, ReturnRecord } from "@/lib/types";

const reasons: { key: ReturnReason; label: string; icon: string }[] = [
  { key: "to_supplier", label: "Al proveedor", icon: "ri-store-2-line" },
  { key: "expired", label: "Vencido", icon: "ri-calendar-close-line" },
  { key: "damaged", label: "Dañado", icon: "ri-alert-line" },
  { key: "defective", label: "Defectuoso", icon: "ri-error-warning-line" },
  { key: "from_customer", label: "De un cliente", icon: "ri-user-received-line" },
  { key: "purchase_error", label: "Error en compra", icon: "ri-file-warning-line" },
  { key: "sale_error", label: "Error en venta", icon: "ri-refund-2-line" },
  { key: "other", label: "Otros motivos", icon: "ri-more-line" },
];

const lossReasons: ReturnReason[] = ["expired", "damaged", "defective"];

function parse(value: string): number {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

export default function NuevaDevolucion() {
  const { data, stock, addReturn } = useData();
  const navigate = useNavigate();

  const [reason, setReason] = useState<ReturnReason>("to_supplier");
  const [productId, setProductId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [currency, setCurrency] = useState<Currency>(data.settings.presentationCurrency);
  const [date, setDate] = useState(todayISO());
  const [counterparty, setCounterparty] = useState("");
  const [restock, setRestock] = useState(false);
  const [refundReceived, setRefundReceived] = useState(false);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options: SelectOption[] = useMemo(
    () =>
      stock.map((s) => ({
        value: s.productId,
        label: s.productName,
        sub: `${number(s.quantity, 0)} ${s.unit}`,
        trailing: money(s.avgCost, s.currency),
      })),
    [stock],
  );

  const selected = stock.find((s) => s.productId === productId);
  const amount = parse(quantity) * parse(unitCost);
  const removesInventory = reason === "to_supplier" || lossReasons.includes(reason);
  const isLoss = lossReasons.includes(reason);
  const isSupplier = reason === "to_supplier";

  const selectProduct = (id: string) => {
    setProductId(id);
    const row = stock.find((s) => s.productId === id);
    if (row) {
      setUnitCost(String(row.avgCost));
      setCurrency(row.currency);
    }
  };

  const save = async () => {
    if (!productId || parse(quantity) <= 0) {
      setError("Selecciona un producto e indica una cantidad válida.");
      return;
    }
    if (selected && parse(quantity) > selected.quantity) {
      setError(`La cantidad supera las existencias (${number(selected.quantity, 0)}).`);
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const lot = data.lots.find((l) => l.productId === productId);
      const record: ReturnRecord = {
        id: uid(),
        type: "return",
        date,
        reason,
        productId,
        productName: selected?.productName ?? "",
        quantity: parse(quantity),
        unitCost: parse(unitCost),
        amount,
        currency,
        exchangeRate: currency === "USD" ? data.settings.exchangeRate : null,
        refundPending: isSupplier ? amount : 0,
        refundReceived: isSupplier ? refundReceived : false,
        restock,
        removesInventory,
        counterparty: counterparty.trim(),
        notes: notes.trim(),
        documentIds: [],
        lotId: lot?.id ?? null,
        createdAt: new Date().toISOString(),
      };
      await addReturn(record);
      navigate("/movimientos");
    } catch (err) {
      setError("No se pudo guardar la devolución. Intenta de nuevo.");
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fr-animate space-y-5 pb-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-background-200/80 text-foreground-700"
        >
          <i className="ri-arrow-left-line text-lg" />
        </button>
        <div>
          <h1 className="text-[20px] font-bold text-foreground-950">Devoluciones y pérdidas</h1>
          <p className="text-[12px] text-foreground-500">Registra el motivo y el impacto</p>
        </div>
      </div>

      <Field label="Motivo">
        <ChipGroup options={reasons} value={reason} onChange={(v) => setReason(v as ReturnReason)} />
      </Field>

      <Field label="Producto">
        <SelectSheet
          value={productId}
          options={options}
          placeholder={options.length === 0 ? "Sin inventario disponible" : "Selecciona un producto"}
          title="Selecciona un producto"
          onChange={selectProduct}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Cantidad">
          <NumberInput
            value={quantity}
            onChange={setQuantity}
            placeholder="0"
            suffix={selected?.unit}
          />
        </Field>
        <Field label="Costo unitario">
          <NumberInput
            value={unitCost}
            onChange={setUnitCost}
            placeholder="0.00"
            prefix={currency === "USD" ? "US$" : "RD$"}
          />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Moneda">
          <CurrencySegment value={currency} onChange={(v) => setCurrency(v as Currency)} />
        </Field>
        <Field label="Fecha">
          <TextInput value={date} onChange={setDate} type="date" icon="ri-calendar-line" />
        </Field>
      </div>

      <Field label={isSupplier ? "Proveedor (opcional)" : "Cliente / responsable (opcional)"}>
        <TextInput
          value={counterparty}
          onChange={setCounterparty}
          placeholder={isSupplier ? "Nombre del proveedor" : "Nombre del cliente"}
          icon="ri-user-3-line"
        />
      </Field>

      <div className="space-y-2 rounded-3xl border border-background-200/70 bg-background-50 p-4 shadow-card">
        {removesInventory ? (
          <label className="flex items-center justify-between gap-3">
            <span className="text-[13px] text-foreground-700">Reintegrar al inventario vendible</span>
            <input
              type="checkbox"
              checked={restock}
              onChange={(e) => setRestock(e.target.checked)}
              className="h-6 w-6 accent-primary-500"
            />
          </label>
        ) : (
          <label className="flex items-center justify-between gap-3">
            <span className="text-[13px] text-foreground-700">El producto vuelve al inventario</span>
            <input
              type="checkbox"
              checked={restock}
              onChange={(e) => setRestock(e.target.checked)}
              className="h-6 w-6 accent-primary-500"
            />
          </label>
        )}
        {isSupplier ? (
          <label className="flex items-center justify-between gap-3 border-t border-background-200 pt-3">
            <span className="text-[13px] text-foreground-700">Reembolso ya recibido</span>
            <input
              type="checkbox"
              checked={refundReceived}
              onChange={(e) => setRefundReceived(e.target.checked)}
              className="h-6 w-6 accent-primary-500"
            />
          </label>
        ) : null}
      </div>

      <div className="rounded-3xl border border-background-200/70 bg-background-50 p-4 shadow-card">
        <div className="space-y-2 text-[13px]">
          <div className="flex justify-between">
            <span className="text-foreground-500">Importe total</span>
            <span className="tabular-nums font-semibold text-foreground-950">
              {money(amount, currency)}
            </span>
          </div>
          <p className="text-[11px] leading-relaxed text-foreground-500">
            {isLoss
              ? "Registra una pérdida por el costo de las unidades afectadas y las saca del inventario vendible."
              : isSupplier
                ? "Reduce el inventario y registra el valor que esperas recuperar del proveedor."
                : "Registra la devolución del dinero al cliente. No se suma como dinero recibido."}
          </p>
        </div>
      </div>

      <Field label="Observaciones (opcional)">
        <TextArea value={notes} onChange={setNotes} placeholder="Detalles de la devolución..." />
      </Field>

      {error ? (
        <p className="rounded-2xl bg-red-50 px-4 py-3 text-[12px] text-red-600">{error}</p>
      ) : null}

      <PrimaryButton
        label={saving ? "Guardando..." : "Guardar devolución"}
        icon="ri-save-3-line"
        onClick={save}
        disabled={saving}
      />
    </div>
  );
}